import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { deletePlan, togglePlanStatus, reorderPlan } from "../../../redux/slice/subscriptionSlice";
import { useToast } from "../../../context/TostContext";
import DeletePopup from "../../../components/delete-popup";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { usePermission } from "../../../hooks/usePermission";
import { TableSearch } from "../../../components/form/Fields";
import CommonButton from "../../../components/commonButton";
import { PlusIcon } from "../../../assets/icons";
import CustomTable from "../../../components/table/CustomTable";
import StatusChip from "../../../components/statusChip/StatusChip";

const MODULE = "subscription_plans";

const List = ({
  data,
  searchTerm,
  setSearchTerm,
  handleEdit,
  handleAdd,
  handleManage,
  refresh,
  pagination,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { hasPermission } = usePermission();
  const [items, setItems] = useState([]);

  React.useEffect(() => {
    setItems(data || []);
  }, [data]);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    if (!hasPermission(MODULE, "update")) {
      toast.error("You don't have permission to reorder plans");
      return;
    }

    const reorderedItems = Array.from(items);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    // Optimistic update
    const updatedWithOrders = reorderedItems.map((item, index) => ({
      ...item,
      plan_order: index + 1
    }));
    setItems(updatedWithOrders);

    try {
      const movedItem = removed;
      const newOrder = result.destination.index + 1;
      const res = await dispatch(reorderPlan({ id: movedItem.id, order: newOrder })).unwrap();
      if (res.success) {
        toast.success("Order updated successfully");
        if (refresh) refresh();
      }
    } catch (error) {
      toast.error(error.message || "Failed to reorder");
      setItems(data || []);
    }
  };

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusItem, setStatusItem] = useState(null);

  const handleConfirmDelete = async () => {
    if (!selectedId) return;
    try {
      setIsDeleting(true);
      const result = await dispatch(deletePlan(selectedId)).unwrap();
      if (result.res.success) {
        toast.success(result.res.message || "Plan deleted");
        setOpenDeleteModal(false);
        setSelectedId(null);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to delete");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!statusItem) return;
    const newStatus = statusItem.status === "active" ? "inactive" : "active";
    try {
      const result = await dispatch(togglePlanStatus({ id: statusItem.id, status: newStatus })).unwrap();
      if (result.res.success) {
        toast.success(result.res.message || "Status updated");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update status");
    } finally {
      setShowStatusModal(false);
      setStatusItem(null);
    }
  };

  const columns = useMemo(() => [
    {
      id: "name",
      header: "NAME",
      accessor: 'name',
      className: "text-start",
    },
    {
      id: "plan_type",
      header: "PLAN TYPE",
      accessor: "plan_type",
      cell: (v) => (<>
        <StatusChip
          bg={v === "free" ? "primary" : "secondary"}
          title={v || "paid"}
          className="text-uppercase fw-semibold"
        />
        {/* <span className={`badge text-uppercase ${v === 'free' ? 'bg-primary' : 'bg-secondary'}`}>
          {v || "paid"}
        </span> */}
      </>

      )
    },
    {
      id: "price",
      header: "PRICE",
      cell: (_, row) => `${row?.price} ${row?.currency || "INR"}`
    },
    {
      id: "interval",
      header: "INTERVAL",
      cell: (_, row) => `${row?.interval_count} ${row?.interval}`,
      className: "text-capitalize"
    },
    {
      id: "trial",
      header: "TRIAL AVAILABLE",
      cell: (_, row) => row?.is_trial_available ? `${row.trial_days} Days` : "No"
    },
    {
      id: "status",
      header: "STATUS",
      accessor: "status",
      cell: (v, row) => (
        <StatusChip
          bg={v === "active" ? "success" : "danger"}
          pill
          title={v === "active" ? "Active" : "Inactive"}
          className={hasPermission(MODULE, "update") ? "cursor-pointer" : ""}
          onClick={() => {
            if (hasPermission(MODULE, "update")) {
              setStatusItem(row);
              setShowStatusModal(true);
            }
          }}
        />
      )
    }
  ], [hasPermission]);

  const actionList = useMemo(() => [
    hasPermission(MODULE, "read") && {
      title: "View",
      handler: (row) => handleEdit(row),
    },
    hasPermission(MODULE, "update") && {
      title: "Manage Features",
      handler: (row) => handleManage(row),
    },
    hasPermission(MODULE, "update") && {
      title: "Edit",
      handler: (row) => handleEdit(row),
    },
    hasPermission(MODULE, "delete") && {
      title: "Delete",
      handler: (row) => {
        setSelectedId(row.id);
        setOpenDeleteModal(true);
      },
    },
  ].filter(Boolean), [hasPermission, handleEdit, handleManage]);

  return (
    <>
      <div className="p-4 d-flex align-items-center justify-content-between gap-4">
        <TableSearch
          value={searchTerm}
          placeholder="Search Plans"
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {hasPermission(MODULE, "create") && (
          <CommonButton
            icon={<PlusIcon />}
            variant="primary"
            title="Add Plan"
            onClick={handleAdd}
          />
        )}
      </div>

      <CustomTable
        data={items}
        columns={columns}
        emptyMessage="No subscription plans found"
        keyField="id"
        actionList={actionList}
        isDragable={hasPermission(MODULE, "update")}
        onDragEnd={handleDragEnd}
        pagination={pagination}
      />

      <DeletePopup
        show={openDeleteModal}
        onHide={() => {
          setOpenDeleteModal(false);
          setSelectedId(null);
        }}
        content="Are you sure you want to delete this plan?"
        removeTitle="Cancel"
        onConfirm={handleConfirmDelete}
        disabled={isDeleting}
        saveTitle={isDeleting ? "Deleting..." : "Delete"}
      />

      <ConfirmModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setStatusItem(null);
        }}
        title={statusItem?.status === "active" ? "Deactivate Plan" : "Activate Plan"}
        message={
          statusItem?.status === "active"
            ? "This plan will be deactivated. Do you want to continue?"
            : "This plan will be activated. Do you want to continue?"
        }
        onConfirm={handleConfirmStatusChange}
      />
    </>
  );
};

export default List;
